import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, Trash2, RefreshCw, Inbox } from "lucide-react";
import { EnquiryTrends } from "@/components/EnquiryTrends";
import { AdminSubmissions, FileButtons, FEE_CHECK_FIELDS } from "@/components/AdminFeeChecks";
import { AdminFactFinds } from "@/components/AdminFactFinds";
import { AdminFactFindDrafts } from "@/components/AdminFactFindDrafts";
import { useIdleLogout } from "@/hooks/useIdleLogout";
import { AdminLoginHistory } from "@/components/AdminLoginHistory";
import { NetWorthSnapshot } from "@/components/NetWorthSnapshot";

const TABS = [
  ["enquiries", "Enquiries"],
  ["fact-finds", "Fact finds"],
  ["client-uploads", "Client uploads"],
  ["fee-checks", "Fee health checks"],
  ["sign-ins", "Sign-in history"],
];

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_META = {
  new: { label: "New", classes: "bg-[#A78558]/10 text-[#A78558] border-[#A78558]/30" },
  contacted: { label: "Contacted", classes: "bg-[#0A2333]/10 text-[#0A2333] border-[#0A2333]/30" },
  closed: { label: "Closed", classes: "bg-black/5 text-[#4A524D] border-black/15" },
};

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-AU", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("enquiries");
  useIdleLogout(navigate);

  const loadEnquiries = useCallback(async () => {
    const { data } = await axios.get(`${API}/enquiries`, { withCredentials: true });
    setEnquiries(data);
  }, []);

  useEffect(() => {
    (async () => {
      try {
        await axios.get(`${API}/auth/me`, { withCredentials: true });
        await loadEnquiries();
        const me = await axios.get(`${API}/auth/me`, { withCredentials: true });
        setUser(me.data);
      } catch {
        navigate("/admin/login");
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate, loadEnquiries]);

  const setStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/enquiries/${id}`, { status }, { withCredentials: true });
      setEnquiries((list) => list.map((e) => (e.id === id ? { ...e, status } : e)));
      toast.success(`Marked as ${STATUS_META[status].label.toLowerCase()}.`);
    } catch {
      toast.error("Couldn't update that enquiry.");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this enquiry permanently?")) return;
    try {
      await axios.delete(`${API}/enquiries/${id}`, { withCredentials: true });
      setEnquiries((list) => list.filter((e) => e.id !== id));
      toast.success("Enquiry deleted.");
    } catch {
      toast.error("Couldn't delete that enquiry.");
    }
  };

  const logout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    navigate("/admin/login");
  };

  const refresh = async () => {
    try {
      await loadEnquiries();
      toast.success("Enquiries refreshed.");
    } catch {
      toast.error("Couldn't refresh enquiries.");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9F8F4] flex items-center justify-center" data-testid="admin-loading">
        <p className="text-sm uppercase tracking-[0.25em] text-[#4A524D]">Loading…</p>
      </div>
    );
  }

  const counts = {
    total: enquiries.length,
    new: enquiries.filter((e) => e.status === "new").length,
    contacted: enquiries.filter((e) => e.status === "contacted").length,
    closed: enquiries.filter((e) => e.status === "closed").length,
  };

  return (
    <div className="min-h-screen bg-[#F9F8F4]" data-testid="admin-dashboard">
      <header className="sticky top-0 z-30 bg-[#F9F8F4]/95 backdrop-blur-sm border-b border-black/10">
        <div className="mx-auto max-w-6xl px-6 md:px-10 h-[72px] flex items-center justify-between">
          <a href="/" data-testid="admin-brand" className="font-serif text-xl tracking-wide text-[#111413]">
            NUA <span className="text-[10px] uppercase tracking-[0.3em] text-[#A78558]">Private Wealth</span>
            <span className="ml-3 font-sans text-[10px] uppercase tracking-[0.25em] text-[#4A524D]">Enquiries</span>
          </a>
          <div className="flex items-center gap-5">
            <span className="hidden sm:block text-xs text-[#4A524D]" data-testid="admin-user-email">{user?.email}</span>
            <button
              data-testid="admin-refresh-button"
              onClick={refresh}
              className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-[#111413] hover:text-[#0A2333] transition-colors duration-300"
            >
              <RefreshCw size={14} />
              Refresh
            </button>
            <button
              data-testid="admin-logout-button"
              onClick={logout}
              className="inline-flex items-center gap-2 rounded-full bg-[#0A2333] px-5 py-2.5 text-xs uppercase tracking-[0.15em] text-[#F9F8F4] transition-[background-color,transform] duration-300 hover:bg-[#071824] hover:-translate-y-0.5"
            >
              <LogOut size={14} />
              Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-6 md:px-10 py-12 md:py-16">
        <h1 className="font-serif text-3xl md:text-4xl tracking-tight text-[#111413]" data-testid="admin-heading">
          Client dashboard
        </h1>

        <nav className="mt-8 flex gap-1 overflow-x-auto border-b border-black/10" data-testid="admin-tabs">
          {TABS.map(([id, label]) => (
            <button
              key={id}
              data-testid={`admin-tab-${id}`}
              onClick={() => setTab(id)}
              className={`shrink-0 -mb-px border-b-2 px-4 py-3 text-xs uppercase tracking-[0.15em] transition-colors duration-200 ${
                tab === id ? "border-[#0A2333] text-[#0A2333]" : "border-transparent text-[#8A8F8B] hover:text-[#111413]"
              }`}
            >
              {label}
            </button>
          ))}
        </nav>

        {tab === "enquiries" && (<>
        <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="admin-stats">
          {[
            { label: "Total", value: counts.total, testid: "stat-total" },
            { label: "New", value: counts.new, testid: "stat-new" },
            { label: "Contacted", value: counts.contacted, testid: "stat-contacted" },
            { label: "Closed", value: counts.closed, testid: "stat-closed" },
          ].map((s) => (
            <div key={s.label} data-testid={s.testid} className="border border-black/10 bg-white px-6 py-5">
              <p className="text-[10px] uppercase tracking-[0.25em] text-[#4A524D]">{s.label}</p>
              <p className="mt-2 font-serif text-3xl text-[#111413]">{s.value}</p>
            </div>
          ))}
        </div>

        {enquiries.length > 0 && <EnquiryTrends enquiries={enquiries} />}

        {enquiries.length === 0 ? (
          <div className="mt-16 flex flex-col items-center gap-4 border border-dashed border-black/15 py-20" data-testid="admin-empty-state">
            <Inbox size={28} className="text-[#4A524D]" />
            <p className="text-sm text-[#4A524D]">No enquiries yet. New submissions from the website will appear here.</p>
          </div>
        ) : (
          <div className="mt-10 space-y-6" data-testid="admin-enquiry-list">
            {enquiries.map((e) => {
              const meta = STATUS_META[e.status] || STATUS_META.new;
              return (
                <article key={e.id} data-testid={`enquiry-card-${e.id}`} className="border border-black/10 bg-white px-6 md:px-8 py-6">
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div>
                      <div className="flex items-center gap-3">
                        <h2 className="font-serif text-2xl tracking-tight text-[#111413]">{e.name}</h2>
                        <span className={`text-[10px] uppercase tracking-[0.2em] border px-2.5 py-1 ${meta.classes}`}>
                          {meta.label}
                        </span>
                      </div>
                    </div>
                    <p className="text-xs text-[#4A524D]">{fmtDate(e.created_at)}</p>
                  </div>

                  <div className="mt-4 flex flex-wrap items-center gap-x-8 gap-y-1 text-sm text-[#4A524D]">
                    <a href={`mailto:${e.email}`} className="hover:text-[#0A2333] transition-colors duration-300">{e.email}</a>
                    {e.phone && <a href={`tel:${e.phone}`} className="hover:text-[#0A2333] transition-colors duration-300">{e.phone}</a>}
                  </div>

                  {(e.age || e.household_income || e.household_super || e.db_fund || e.db_fund_value || e.debt_level || e.other_assets || e.cost_of_living) && (
                    <div data-testid={`enquiry-facts-${e.id}`} className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-[11px] uppercase tracking-[0.15em] text-[#A78558]">
                      {e.age && <span>Age: {e.age}</span>}
                      {e.household_income && <span>Income: {e.household_income}</span>}
                      {e.household_super && <span>Super: {e.household_super}</span>}
                      {e.db_fund && <span>DB fund: {e.db_fund}</span>}
                      {e.db_fund_value && <span>DB value: {e.db_fund_value}</span>}
                      {e.debt_level && <span>Debt: {e.debt_level}</span>}
                      {e.other_assets && <span>Other assets: {e.other_assets}</span>}
                      {e.cost_of_living && <span>Cost of living: {e.cost_of_living}</span>}
                    </div>
                  )}
                  <NetWorthSnapshot enquiry={e} />

                  <p className="mt-4 text-sm leading-relaxed text-[#111413] border-l-2 border-[#0A2333]/30 pl-4 max-w-3xl">
                    {e.message}
                  </p>
                  <FileButtons kind="enquiries" item={e} />

                  <div className="mt-6 flex flex-wrap gap-3">
                    {e.status !== "contacted" && (
                      <button
                        data-testid={`enquiry-mark-contacted-${e.id}`}
                        onClick={() => setStatus(e.id, "contacted")}
                        className="text-[11px] uppercase tracking-[0.15em] border border-[#0A2333]/40 text-[#0A2333] px-4 py-2 transition-[background-color,color] duration-300 hover:bg-[#0A2333] hover:text-[#F9F8F4]"
                      >
                        Mark contacted
                      </button>
                    )}
                    {e.status !== "closed" && (
                      <button
                        data-testid={`enquiry-mark-closed-${e.id}`}
                        onClick={() => setStatus(e.id, "closed")}
                        className="text-[11px] uppercase tracking-[0.15em] border border-black/20 text-[#111413] px-4 py-2 transition-[background-color,color] duration-300 hover:bg-[#111413] hover:text-[#F9F8F4]"
                      >
                        Mark closed
                      </button>
                    )}
                    {e.status !== "new" && (
                      <button
                        data-testid={`enquiry-mark-new-${e.id}`}
                        onClick={() => setStatus(e.id, "new")}
                        className="text-[11px] uppercase tracking-[0.15em] border border-black/20 text-[#4A524D] px-4 py-2 transition-colors duration-300 hover:text-[#111413]"
                      >
                        Reopen
                      </button>
                    )}
                    <button
                      data-testid={`enquiry-delete-${e.id}`}
                      onClick={() => remove(e.id)}
                      className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.15em] text-[#A78558] px-4 py-2 transition-colors duration-300 hover:text-[#8A6A43]"
                    >
                      <Trash2 size={13} />
                      Delete
                    </button>
                  </div>
                </article>
              );
            })}
          </div>
        )}
        </>)}
        {tab === "fact-finds" && (
          <>
            <AdminFactFindDrafts fmtDate={fmtDate} />
            <AdminFactFinds fmtDate={fmtDate} />
          </>
        )}
        {tab === "client-uploads" && (
          <AdminSubmissions kind="client-uploads" title="" empty="No client documents uploaded yet." fmtDate={fmtDate} />
        )}
        {tab === "fee-checks" && (
          <AdminSubmissions
            kind="fee-checks"
            title=""
            empty="No fee health check requests yet."
            fields={FEE_CHECK_FIELDS}
            fmtDate={fmtDate}
          />
        )}
        {tab === "sign-ins" && <AdminLoginHistory fmtDate={fmtDate} />}
      </main>
    </div>
  );
}
